import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactSelect, { components } from "react-select";
import { AiOutlineCheck } from "react-icons/ai";
import { BiCloudDownload } from "react-icons/bi";
import toast from "react-hot-toast";
import { CustomHorizontalBarChart } from "../../common/CustomHorizontalBarChart";
import CustomDetailModal from "./CustomDetailModal";
import Spinner from "../Spinner";
import {
  getValidatedPendingZones,
  getValidatedPendingRegions,
  getValidatedPendingFleetsHubs,
  getValidatedPendingZoneDueRangeTyres,
  getValidatedPendingRegionDueRangeTyres,
  getValidatedPendingHubDueRangeTyres,
  downloadValidatedPendingZoneDueRangeTyres,
  downloadValidatedPendingRegionDueRangeTyres,
  downloadValidatedPendingHubDueRangeTyres,
} from "../../apis/scrappedCasingTyres.service";
import { exportScrapCasingTyresExcel } from "../../utils/exportExcel";
import {
  VALIDATED_AGE_BUCKET_COLORS,
  VALIDATED_PENDING_AGE_BUCKETS,
  SCRAP_DASHBOARD_TYRE_STATUS_OPTIONS,
  VALIDATED_PENDING_DETAIL_MODAL_COLUMNS,
  isScrapTyreStatus,
  resolveCasingTypeDisplay,
} from "utils/scrapConstants";

const styles = {
  container: "w-full max-w-[1400px]",
  loadingWrapper:
    "w-full flex justify-center items-center h-[680px] bg-white rounded-3xl border border-[#E9DFFA] shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-6",
  filterWrapper: "flex flex-wrap items-center gap-4 justify-end",
  optionRow: "flex items-center justify-between gap-2 overflow-hidden",
  optionLabel: "truncate",
  checkIcon: "text-green-600 flex-shrink-0",
};

const CustomOption = (props) => (
  <components.Option {...props}>
    <div className={styles.optionRow}>
      <span className={styles.optionLabel}>{props.label}</span>
      {props.isSelected && <AiOutlineCheck className={styles.checkIcon} />}
    </div>
  </components.Option>
);

const reactSelectCustomStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "36px",
    minWidth: "200px",
    borderRadius: "0.375rem",
    borderColor: "#858585",
    fontSize: "0.875rem",
    fontWeight: "500",
    color: "#2C2C2C",
    "&:hover": {
      borderColor: "#6B7280",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 8px",
    flexWrap: "nowrap",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#2C2C2C",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#22C55E"
      : state.isFocused
        ? "#F3F4F6"
        : "white",
    color: state.isSelected ? "white" : "#2C2C2C",
  }),
  dropdownIndicator: (provided) => ({ ...provided, padding: "0 8px" }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "220px",
  }),
};

const datalabelsFormatter = (value) => (value > 0 ? value : "");

const formatDetailDate = (value) => {
  if (value == null || value === "") return "-";
  const t = new Date(value);
  return Number.isNaN(t.getTime()) ? String(value) : t.toLocaleDateString("en-GB");
};

const getBucketArray = (item, tyreStatus) => {
  if (!item) return [];
  if (tyreStatus === "CASING") {
    return item.casingTyres || item.tyres || [];
  }
  return item.scrapTyres || item.tyres || [];
};

/** Same body shape for list + download APIs per drill-down level */
const pendingDueRangeRequest = (detail, activeRegionCode) => {
  if (!detail) return null;
  const dueRange = detail.ageBucket;
  const tyreStatus = detail.tyreStatus;
  if (detail.viewMode === "zone") {
    const zoneName = detail.zoneName || detail.resource;
    if (!zoneName) return null;
    return { type: "zone", body: { zoneName, dueRange, tyreStatus } };
  }
  if (detail.viewMode === "region") {
    const rc = detail.regionCode;
    if (rc == null || rc === "") return null;
    return { type: "region", body: { regionCode: String(rc), dueRange, tyreStatus } };
  }
  const hubId = detail.hubId;
  const regionCode = detail.regionCode ?? activeRegionCode;
  if (hubId == null || hubId === "" || regionCode == null || regionCode === "") {
    return null;
  }
  return {
    type: "hub",
    body: {
      hubId: String(hubId),
      regionCode: String(regionCode),
      dueRange,
      tyreStatus,
    },
  };
};

const ValidatedTyresPendingBillingGraph = () => {
  const [viewModeValidated, setViewModeValidated] = useState("zone");
  const [tyreStatus, setTyreStatus] = useState("ALL");

  const [zones, setZones] = useState([]);
  const [regions, setRegions] = useState([]);
  const [fleets, setFleets] = useState([]);
  const [activeZoneName, setActiveZoneName] = useState(null);
  const [activeRegionCode, setActiveRegionCode] = useState(null);

  const [loading, setLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (!selectedDetail) {
      setModalData([]);
      return;
    }

    const fetchModalData = async () => {
      const req = pendingDueRangeRequest(selectedDetail, activeRegionCode);
      if (!req) {
        setModalData([]);
        return;
      }

      setModalLoading(true);
      try {
        const byType = {
          zone: getValidatedPendingZoneDueRangeTyres,
          region: getValidatedPendingRegionDueRangeTyres,
          hub: getValidatedPendingHubDueRangeTyres,
        };
        const response = await byType[req.type](req.body);
        const rows = response?.data ?? response ?? [];
        setModalData(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("Error fetching validated pending due-range tyres:", error);
        setModalData([]);
      } finally {
        setModalLoading(false);
      }
    };

    fetchModalData();
  }, [selectedDetail, activeRegionCode]);

  const getFilterPayload = useCallback(
    () => ({ tyreStatus }),
    [tyreStatus],
  );

  useEffect(() => {
    const fetchZones = async () => {
      setLoading(true);
      try {
        const response = await getValidatedPendingZones(getFilterPayload());
        const zoneData = response?.data || response || [];
        setZones(Array.isArray(zoneData) ? zoneData : []);
      } catch (error) {
        console.error("Error fetching validated pending zones:", error);
        setZones([]);
      } finally {
        setLoading(false);
      }
    };
    fetchZones();
    setViewModeValidated("zone");
    setRegions([]);
    setFleets([]);
    setActiveZoneName(null);
    setActiveRegionCode(null);
  }, [getFilterPayload]);

  const handleZoneClick = useCallback(
    async (zoneName) => {
      setLoading(true);
      setActiveZoneName(zoneName);
      try {
        const response = await getValidatedPendingRegions({
          ...getFilterPayload(),
          zoneName,
        });
        const regionData = response?.data || response || [];
        setRegions(Array.isArray(regionData) ? regionData : []);
        setViewModeValidated("region");
      } catch (error) {
        console.error("Error fetching validated pending regions:", error);
        setRegions([]);
      } finally {
        setLoading(false);
      }
    },
    [getFilterPayload],
  );

  const handleRegionClick = useCallback(
    async (regionCode) => {
      if (regionCode == null || regionCode === "") return;
      setLoading(true);
      setActiveRegionCode(String(regionCode));
      try {
        const response = await getValidatedPendingFleetsHubs({
          ...getFilterPayload(),
          regionCode: String(regionCode),
        });
        const fleetData = response?.data || response || [];
        if (
          Array.isArray(fleetData) &&
          fleetData.length > 0 &&
          fleetData[0].hubs
        ) {
          setFleets(fleetData[0].hubs);
        } else {
          setFleets(Array.isArray(fleetData) ? fleetData : []);
        }
        setViewModeValidated("fleet");
      } catch (error) {
        console.error("Error fetching validated pending fleets:", error);
        setFleets([]);
      } finally {
        setLoading(false);
      }
    },
    [getFilterPayload],
  );

  const getValidatedChartData = useCallback(() => {
    let rawDataList = [];
    let activeCrumb;
    let nextMode;
    let labelKey;
    let yAxisTitle;

    if (viewModeValidated === "zone") {
      rawDataList = zones;
      yAxisTitle = "Zones";
      activeCrumb = 0;
      nextMode = "region";
      labelKey = "zoneName";
    } else if (viewModeValidated === "region") {
      rawDataList = regions;
      yAxisTitle = "Regions";
      activeCrumb = 1;
      nextMode = "fleet";
      labelKey = "regionName";
    } else {
      rawDataList = fleets;
      yAxisTitle = "Fleets & Hubs";
      activeCrumb = 2;
      nextMode = null;
      labelKey = "hubName";
    }

    const breadcrumbs = [
      {
        label: "Zone Wise",
        onClick:
          activeCrumb > 0 ? () => setViewModeValidated("zone") : undefined,
        active: activeCrumb === 0,
      },
      ...(activeCrumb >= 1
        ? [
            {
              label: "Region Wise",
              onClick:
                activeCrumb > 1
                  ? () => setViewModeValidated("region")
                  : undefined,
              active: activeCrumb === 1,
            },
          ]
        : []),
      ...(activeCrumb >= 2
        ? [{ label: "Fleet & Hub Wise", active: true }]
        : []),
    ];

    const labels = rawDataList.map((d) => {
      const name =
        d[labelKey] ||
        d.fleetName ||
        d.hubName ||
        d.zoneName ||
        d.regionName ||
        "Unknown";
      // Two lines at hub level: name on top, hub code below it.
      if (viewModeValidated === "fleet" && d.hubCode) {
        return [name, `(${d.hubCode})`];
      }
      return name;
    });

    // "All" splits each category into two side-by-side bars (separate stacks):
    // one for scrap, one for casing. Any other status keeps the single stack.
    const isAll = tyreStatus === "ALL";

    const buildBucketData = (item, arrKey, bucketId) => {
      const buckets = item?.[arrKey] || [];
      const match = buckets.find((b) => b._id === bucketId);
      return match ? match.count : 0;
    };

    const makeStackDatasets = (stackId, statusValue, arrKey) =>
      VALIDATED_PENDING_AGE_BUCKETS.map((bucket, idx) => ({
        label: bucket.label,
        stack: stackId,
        stackStatus: statusValue,
        bucketId: bucket.id,
        // Keep a clear gap between the scrap and casing bars (barPercentage)
        // and between zone groups (categoryPercentage).
        categoryPercentage: 0.85,
        barPercentage: 0.7,
        data: rawDataList.map((d) => buildBucketData(d, arrKey, bucket.id)),
        backgroundColor: VALIDATED_AGE_BUCKET_COLORS[idx],
      }));

    const datasets = isAll
      ? [
          ...makeStackDatasets("scrap", "SCRAP", "scrapTyres"),
          ...makeStackDatasets("casing", "CASING", "casingTyres"),
        ]
      : VALIDATED_PENDING_AGE_BUCKETS.map((bucket, idx) => ({
          label: bucket.label,
          data: rawDataList.map((d) => {
            const buckets = getBucketArray(d, tyreStatus);
            const match = buckets.find((b) => b._id === bucket.id);
            return match ? match.count : 0;
          }),
          bucketId: bucket.id,
          backgroundColor: VALIDATED_AGE_BUCKET_COLORS[idx],
        }));

    const legendItems = VALIDATED_PENDING_AGE_BUCKETS.map((bucket, idx) => ({
      label: bucket.label,
      color: VALIDATED_AGE_BUCKET_COLORS[idx],
    }));

    // "Scrap"/"Casing" markers drawn at the right end of each bar in All mode.
    const stackEndLabels = isAll
      ? [
          { stack: "scrap", label: "Scrap", color: "#6B7280" },
          { stack: "casing", label: "Casing", color: "#6B7280" },
        ]
      : undefined;

    // In All mode the two stacks are separate bars, so the axis only needs to fit
    // the larger of the scrap/casing totals per category (not their sum).
    const maxStackedValue = labels.reduce((max, _, i) => {
      let sum;
      if (isAll) {
        const stackSum = (stackId) =>
          datasets
            .filter((ds) => ds.stack === stackId)
            .reduce((acc, ds) => acc + (Number(ds.data[i]) || 0), 0);
        sum = Math.max(stackSum("scrap"), stackSum("casing"));
      } else {
        sum = datasets.reduce((acc, ds) => acc + (Number(ds.data[i]) || 0), 0);
      }
      return Math.max(max, sum);
    }, 0);

    let stepSize = 100;
    if (maxStackedValue > 1500) stepSize = 500;
    else if (maxStackedValue > 1000) stepSize = 250;
    else if (maxStackedValue >= 300) stepSize = 100;
    else if (maxStackedValue >= 100) stepSize = 25;
    else stepSize = 10;

    let dynamicMax = Math.ceil((maxStackedValue || 1) / stepSize) * stepSize;
    // Reserve headroom on the right so the end-of-bar Scrap/Casing labels fit.
    if (isAll) dynamicMax += stepSize;

    return {
      labels,
      datasets,
      legendItems,
      stackEndLabels,
      barThickness: isAll ? null : 48,
      yAxisTitle,
      breadcrumbs,
      rawDataList,
      onCategoryClick: nextMode
        ? async (_event, elements) => {
            if (!elements || elements.length === 0) return;
            const localIndex = elements[0].index;
            const row = rawDataList[localIndex] || {};
            if (viewModeValidated === "zone") {
              await handleZoneClick(row.zoneName);
            } else if (viewModeValidated === "region") {
              const code = row.regionCode;
              if (code == null || code === "") return;
              await handleRegionClick(code);
            }
          }
        : null,
      onBarClick: (_event, elements) => {
        if (!elements || elements.length === 0) return;
        const localIndex = elements[0].index;
        const datasetIndex = elements[0].datasetIndex;
        const row = rawDataList[localIndex] || {};
        const ds = datasets[datasetIndex] || {};
        const bucket =
          VALIDATED_PENDING_AGE_BUCKETS.find((b) => b.id === ds.bucketId) ||
          VALIDATED_PENDING_AGE_BUCKETS[datasetIndex];
        // In All mode the clicked bar's own stack decides scrap vs casing.
        const clickStatus = ds.stackStatus || tyreStatus;

        const labelPrefix =
          viewModeValidated === "zone"
            ? row.zoneName
            : viewModeValidated === "region"
              ? row.regionName || row.regionCode
              : `${row.hubName || row.fleetName}${row.hubCode ? ` (${row.hubCode})` : ""}`;

        setSelectedDetail({
          title: `${labelPrefix} - ${bucket.label} (${clickStatus === "CASING" ? "Casing" : "Scrap"})`,
          resource: labelPrefix,
          ageBucket: bucket.id,
          tyreStatus: clickStatus,
          viewMode: viewModeValidated,
          zoneName: row.zoneName || activeZoneName,
          regionCode: row.regionCode ?? activeRegionCode,
          hubId: row.hubId || row._id,
          count: ds.data[localIndex],
        });
      },
      xAxisStepSize: stepSize,
      xAxisMax: dynamicMax,
    };
  }, [
    viewModeValidated,
    tyreStatus,
    zones,
    regions,
    fleets,
    activeZoneName,
    activeRegionCode,
    handleZoneClick,
    handleRegionClick,
  ]);

  // Memoized so unrelated re-renders (e.g. opening the detail modal) keep the
  // same labels/datasets identities — otherwise Chart.js treats it as new data
  // and replays the grow animation, making the graph look like it reloads.
  const validatedConfig = useMemo(
    () => getValidatedChartData(),
    [getValidatedChartData],
  );

  const selectedStatusOption =
    SCRAP_DASHBOARD_TYRE_STATUS_OPTIONS.find((o) => o.value === tyreStatus) ||
    SCRAP_DASHBOARD_TYRE_STATUS_OPTIONS[0];

  const resolveDownloadRows = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.data)) return response.data;
    return [];
  };

  const handleDownload = async () => {
    if (!selectedDetail) return;
    const req = pendingDueRangeRequest(selectedDetail, activeRegionCode);
    if (!req) {
      toast.error("Missing zone, region, or hub context for download");
      return;
    }
    setDownloadLoading(true);
    try {
      const downloadByType = {
        zone: downloadValidatedPendingZoneDueRangeTyres,
        region: downloadValidatedPendingRegionDueRangeTyres,
        hub: downloadValidatedPendingHubDueRangeTyres,
      };
      const response = await downloadByType[req.type](req.body);
      const list = resolveDownloadRows(response);
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      const safe = `${selectedDetail.resource}-${selectedDetail.ageBucket}-${selectedDetail.tyreStatus}`
        .replace(/[/\\?%*:|"<>]/g, "-")
        .replace(/\s+/g, "_");
      exportScrapCasingTyresExcel(list, `validated-pending-billing_${safe}`, {
        tyreStatus: selectedDetail.tyreStatus,
      });
      toast.success("File downloaded");
    } catch (e) {
      console.error("Download validated pending tyres failed:", e);
    } finally {
      setDownloadLoading(false);
    }
  };

  // Scrap tyres have no casing grade, so their Casing Type cell shows "SRP".
  const isScrapDetail = isScrapTyreStatus(selectedDetail?.tyreStatus);

  const chartFilters = (
    <div className={styles.filterWrapper}>
      <div className="w-full md:w-auto min-w-[200px]">
        <ReactSelect
          options={SCRAP_DASHBOARD_TYRE_STATUS_OPTIONS}
          value={selectedStatusOption}
          onChange={(opt) => opt && setTyreStatus(opt.value)}
          components={{ Option: CustomOption }}
          placeholder="Select Status"
          styles={reactSelectCustomStyles}
          isSearchable={false}
        />
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      {loading ? (
        <div className={styles.loadingWrapper}>
          <Spinner />
        </div>
      ) : (
        <CustomHorizontalBarChart
          title="Validated Tyres Pending for Billing"
          titleTextSize="22px"
          breadcrumbs={validatedConfig.breadcrumbs}
          labels={validatedConfig.labels}
          datasets={validatedConfig.datasets}
          legendItems={validatedConfig.legendItems}
          stackEndLabels={validatedConfig.stackEndLabels}
          xAxisTitle="tyres"
          yAxisTitle={validatedConfig.yAxisTitle}
          showYAxisTitle={true}
          onCategoryClick={validatedConfig.onCategoryClick}
          onBarClick={validatedConfig.onBarClick}
          hoverCursor={true}
          tooltipChartDismissDelayMs={900}
          tooltipPointerLeaveDelayMs={750}
          xAxisMax={validatedConfig.xAxisMax}
          xAxisStepSize={validatedConfig.xAxisStepSize}
          paddingTop="34px"
          paddingLeft="40px"
          paddingRight="40px"
          paddingBottom="45px"
          chartHeight="520px"
          barThickness={validatedConfig.barThickness}
          minBarLength={10}
          yAxisClickBandLeftPadding={18}
          yAxisClickBandRightPadding={22}
          topRightAction={chartFilters}
          topRightActionPlacement="breadcrumbs"
          datalabelsFormatter={datalabelsFormatter}
        />
      )}

      <CustomDetailModal
        isOpen={!!selectedDetail}
        onClose={() => setSelectedDetail(null)}
        title={selectedDetail?.title}
        modalWidth="85vw"
        tableWrapperClassName="min-w-[1200px]"
        filterComponent={
          <button
            type="button"
            className="flex items-center gap-2 bg-[#22C55E] text-white px-5 py-2.5 rounded-xl hover:bg-green-600 transition-all shadow-sm hover:shadow-md font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={downloadLoading || modalLoading || !selectedDetail}
            onClick={handleDownload}
          >
            <BiCloudDownload size={20} className={downloadLoading ? "animate-pulse" : ""} />
            <span>{downloadLoading ? "Preparing…" : "Download Data"}</span>
          </button>
        }
        columns={VALIDATED_PENDING_DETAIL_MODAL_COLUMNS}
        loading={modalLoading}
        data={modalData.map((d, idx) => {
          const due =
            d.DueDays ?? d.dueDays ?? d.dueDay ?? d.dueDaysCount ?? d.daysDue;
          return {
            id: d._id ?? d.id ?? idx,
            zoneName: d.zoneName ?? "-",
            regionName: d.regionName ?? "-",
            hubName: d.hubName ?? "-",
            stencilNo: d.stencilNo ?? d.stencilNumber ?? "-",
            lastInspectionDate: formatDetailDate(
              d.lastInspectionDate ?? d.lastInspDate,
            ),
            tubeType:
              d.tubeType ??
              d.tubeTypeTubeless ??
              d.productType ??
              d.tube_vs_tubeless ??
              "-",
            casingType: resolveCasingTypeDisplay(d.casingType ?? d.casing_type, isScrapDetail || isScrapTyreStatus(d.status ?? d.tyreStatus)),
            validationDate: formatDetailDate(
              d.validationDate ?? d.validatedAt,
            ),
            validatedBy: d.validatedBy ?? d.validated_by ?? "-",
            dueDays:
              due !== undefined && due !== null && due !== ""
                ? due
                : "-",
          };
        })}
      />
    </div>
  );
};

export default ValidatedTyresPendingBillingGraph;
